/**
 * Beta-Safe Execution Shim
 * ─────────────────────────
 * Lightweight, non-destructive layer that standardizes how intelligence-driven
 * actions behave across a *limited* set of surfaces.
 *
 * SCOPE (per spec):
 *  - Operations Command Center (primaryAction)
 *  - Dispatch Intelligence (nextAction CTA)
 *  - What Needs Attention (already aligned — uses same patterns)
 *
 * NON-GOALS:
 *  - No UI changes, no new components, no schema overhaul.
 *  - Existing actions without `executionMode` keep working untouched.
 */

import type { NavigateFunction } from "react-router-dom";

export type ExecutionMode = "direct" | "modal" | "navigate";

/** Minimal action shape — additive, all fields optional except label. */
export interface ExecutableAction {
  label: string;
  executionMode?: ExecutionMode;
  /** For "direct" — runs immediately. May be async. */
  handler?: () => void | Promise<void>;
  /** For "modal" — opens a modal; consumer wires payload into its own modal state. */
  openModal?: (payload?: unknown) => void;
  /** For "navigate" or fallback. */
  route?: string;
  /** Optional context passed to handler/openModal. */
  payload?: unknown;
}

export interface RunActionContext {
  navigate?: NavigateFunction;
  onError?: (err: unknown) => void;
}

/**
 * Universal runner. Safe with undefined/legacy actions — never throws.
 * Returns true if the runner handled the action; false if caller should
 * fall back to its existing onClick / Link behavior.
 */
export async function runAction(
  action: ExecutableAction | undefined | null,
  ctx: RunActionContext = {},
): Promise<boolean> {
  if (!action) return false;

  const mode: ExecutionMode =
    action.executionMode ??
    (action.handler ? "direct" : action.openModal ? "modal" : "navigate");

  try {
    if (mode === "direct" && action.handler) {
      await action.handler();
      return true;
    }
    if (mode === "modal" && action.openModal) {
      action.openModal(action.payload);
      return true;
    }
    if (mode === "navigate" && action.route && ctx.navigate) {
      ctx.navigate(action.route);
      return true;
    }
    // Nothing matched — let caller fall back.
    return false;
  } catch (err) {
    ctx.onError?.(err);
    // Don't crash UI — fallback to legacy behavior.
    return false;
  }
}
